import { useEffect, useState } from 'react'

import { type SearchHit, search } from '../api/search'
import { Button } from './Control'
import { Waiting } from './Status'
import { Hit } from '../views/search/Hit'

/**
 * Choosing something that already exists, wherever MyLib needs one thing chosen.
 *
 * **It asks the one search there is**, rather than offering a second browser over the library. That
 * is not a shortcut: what somebody is doing is "find the thing I mean", which is the question ⌘K
 * already answers over everything — documents, notes, open points, cards, sessions — with one query
 * language and no list per area. Building a folder tree into this panel would be a second way of
 * finding things, kept in step with the first by hand.
 *
 * Nothing is offered until something is typed. A default list would have to decide what "recent"
 * means for six kinds of content at once, and the honest answer to an empty field is that nobody has
 * said what they are looking for yet.
 *
 * **And the hits are drawn by the search's own row.** They were a line of text on the board for a
 * while, which meant the same document looked like a first page in one place and like a file name in
 * another — the very thing this product is meant not to do. One row, one appearance: the picture, the
 * passage the words were found in, the kind, the folder.
 *
 * It lives in `ui/` because two areas choose content: the desk lays a card down, and a session links
 * something to itself. What differs between them is [kinds] and the words, and nothing else.
 */
export interface PickedContent {
  /** Lower case, as the search names a kind: `document`, `note`, `task`, `flashcard`, `appointment`. */
  readonly kind: string
  readonly id: string
  /** What to write where the thing itself cannot be drawn. */
  readonly label: string
}

export interface ContentPickerProps {
  /** Which kinds may be chosen. A hit of any other kind is not offered at all. */
  readonly kinds: readonly string[]
  /** What the field says before anything is typed. */
  readonly placeholder: string
  /** Names the field for a screen reader, and says what choosing does. */
  readonly label: string
  /** One sentence under the empty field, saying what is being searched. */
  readonly lead: string
  /** Ids that are already chosen, so they are shown as such rather than offered again. */
  readonly already?: readonly string[]
  readonly onChoose: (what: PickedContent) => void
  readonly onClose: () => void
  /** Whether the field takes the focus as it appears. True where a control opened it. */
  readonly autoFocus?: boolean
}

export function ContentPicker({
  kinds,
  placeholder,
  label,
  lead,
  already = [],
  onChoose,
  onClose,
  autoFocus = true,
}: ContentPickerProps) {
  const [line, setLine] = useState('')
  const [hits, setHits] = useState<readonly SearchHit[]>([])
  const [asking, setAsking] = useState(false)

  const asked = line.trim()
  // What is shown, rather than what was last fetched: an emptied field shows nothing at once and
  // does not have to wait for a request to answer with nothing.
  const shown = asked === '' ? [] : hits

  useEffect(() => {
    if (asked === '') {
      return
    }
    const controller = new AbortController()
    // A moment after the typing stops, so that a word is one request rather than one per character.
    const timer = setTimeout(() => {
      setAsking(true)
      search({ line: asked, limit: MOST_SHOWN }, controller.signal)
        .then((found) => {
          setHits(found.hits)
          setAsking(false)
        })
        .catch(() => {
          // A search that failed is an empty list here. What went wrong belongs on the screen that
          // is *about* searching, not in a panel somebody opened to choose something.
          setHits([])
          setAsking(false)
        })
    }, ASK_AFTER_MS)
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [asked])

  const offered = shown
    .map((hit) => ({ hit, what: whatItIs(hit) }))
    .filter((row) => row.what !== null && kinds.includes(row.what.kind))

  return (
    <div className="picker" role="search">
      <div className="picker__head">
        <input
          className="picker__field"
          type="search"
          autoFocus={autoFocus}
          value={line}
          aria-label={label}
          placeholder={placeholder}
          onChange={(event) => {
            setLine(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              onClose()
            }
          }}
        />
        <Button tone="bare" icon="close" iconOnlyLabel="Close" onClick={onClose} />
      </div>

      {/*
        Said before anything is typed, because an empty field with a magnifier in it does not say
        *what* is being searched — and what is being searched here is only what you already have.
      */}
      {asked === '' && <p className="picker__lead">{lead}</p>}

      {asking && <Waiting label="Looking." />}

      {asked !== '' && !asking && offered.length === 0 && (
        <p className="picker__nothing">Nothing of yours matches that.</p>
      )}

      {asked !== '' && !asking && offered.length > 0 && (
        <ul className="hits picker__hits" role="listbox" aria-label={label}>
          {offered.map(({ hit, what }, index) => {
            const chosen = what !== null && already.includes(what.id)
            return (
              <Hit
                key={`${what?.kind ?? ''}-${what?.id ?? String(index)}`}
                hit={hit}
                folder={chosen ? 'Already here' : ''}
                active={false}
                id={`picker-${String(index)}`}
                onOpen={() => {
                  if (what !== null && !chosen) {
                    onChoose(what)
                  }
                }}
              />
            )
          })}
        </ul>
      )}
    </div>
  )
}

/**
 * What a hit is, in the two things a caller needs: which kind of content, and what to write on it.
 *
 * A hit on something written on a board is deliberately never one of these — a board element is part
 * of a board, and neither a card nor a link points at one. A subject is not one either: putting a
 * bracket around a set of things is what subjects are, and choosing one here would be a second way to
 * say it.
 */
export function whatItIs(hit: SearchHit): PickedContent | null {
  if (hit.document !== null && hit.document !== undefined) {
    return { kind: 'document', id: hit.document.id, label: hit.document.title }
  }
  if (hit.note !== null && hit.note !== undefined) {
    return { kind: 'note', id: hit.note.id, label: firstLine(hit.note.text) }
  }
  if (hit.task !== null && hit.task !== undefined) {
    return { kind: 'task', id: hit.task.id, label: hit.task.text }
  }
  if (hit.flashcard !== null && hit.flashcard !== undefined) {
    return {
      kind: 'flashcard',
      id: hit.flashcard.id,
      label:
        hit.flashcard.askedFor === '' ? firstLine(hit.flashcard.front) : hit.flashcard.askedFor,
    }
  }
  if (hit.appointment !== null && hit.appointment !== undefined) {
    return { kind: 'appointment', id: hit.appointment.id, label: hit.appointment.title }
  }
  return null
}

function firstLine(text: string): string {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  )
}

const MOST_SHOWN = 12

/** Long enough that a typed word is one request; short enough that the list keeps up with typing. */
const ASK_AFTER_MS = 250
